import React from 'react';

import InsightScreenshot, {
  type InsightScreenshotComment,
} from 'containers/admin/clients/client/tabs-components/insights-tab/insight-details/components/insight-screenshot';
import PdfContextData from 'containers/admin/clients/client/tabs-components/insights-tab/insight-details/components/context-data-panel/pdf-context-data';

import { formatDate } from 'helpers/dates-formatter';

import type { InsightDto } from 'api/generated/schemas/insightDto';
import type { UserInsightDto } from 'api/generated/schemas/userInsightDto';

import Icons from 'assets/icons';

export type PdfContentInsight = InsightDto | UserInsightDto;

export interface PdfContentProps {
  insight: PdfContentInsight;
  comments?: InsightScreenshotComment[];
  showHeader?: boolean;
  showViewReport?: boolean;
}

const PdfContent = React.forwardRef<HTMLDivElement, PdfContentProps>(
  (
    { insight, comments = [], showHeader = true, showViewReport = false },
    ref
  ) => (
    <div ref={ref} className="bg-white p-20 w-[1200px]">
      {showHeader && (
        <>
          <Icons.LoginLogo className="h-20 w-60 mb-5" />

          <div className="mb-8 pb-6 border-b border-gray-200">
            <h1 className="text-4xl font-bold text-navy-800 mb-4">
              {insight.name}
            </h1>
            <div className="flex items-center text-gray-700 gap-x-6">
              <div className="flex items-center gap-2">
                <Icons.Calendar className="size-5" />
                <div className="font-semibold">
                  {formatDate(insight.createdDate, 'MMM D, YYYY')}
                </div>
              </div>
              <div>
                <span className="text-gray-600 text-sm">Created by: </span>
                <span className="text-gray-700 text-sm font-semibold">
                  {insight.createdBy || 'Unknown'}
                </span>
              </div>
            </div>
          </div>

          {insight?.description && (
            <div className="bg-blue-50 border border-blue-300 rounded-md p-5 mb-8">
              <div className="flex items-center gap-2 mb-2">
                <Icons.Idea customClass="size-5 text-blue-700" />
                <div className="text-xl font-semibold text-blue-700">
                  Summary
                </div>
              </div>
              <p className="text-gray-700">{insight.description}</p>
            </div>
          )}
        </>
      )}

      {insight?.imageUrl && (
        <div className="mb-8">
          <div className="flex justify-center items-center border border-gray-200 rounded-lg bg-white mx-auto max-w-[800px]">
            <InsightScreenshot
              imageUrl={insight.imageUrl}
              comments={comments}
              selectedCommentId={null}
              onCommentClick={() => {}}
            />
          </div>
        </div>
      )}

      {comments.length > 0 && (
        <div className="mb-8">
          <div className="space-y-4">
            {comments.map((comment, index) => {
              const commentNumber = index + 1;

              return (
                <div key={comment.id} className="flex items-start gap-3">
                  <div className="size-8 rounded-full flex items-center justify-center text-white font-bold bg-navy-400 shrink-0 mt-0.5">
                    {commentNumber}
                  </div>
                  <p className="text-gray-700 flex-1 leading-relaxed pt-1.5">
                    {comment.message}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <PdfContextData insight={insight} showViewReport={showViewReport} />
    </div>
  )
);

PdfContent.displayName = 'PdfContent';

export default PdfContent;
